import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { api } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Orders = () => {
  const navigate = useNavigate();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { success, warning } = useToast();

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/orders', { params: { excludeReturns: true, ...(statusFilter !== 'all' && { status: statusFilter }) } });
        const orders = (response.data?.data || []).map(order => ({
          id: order._id || order.id,
          customerName: order.userId?.name || order.customerName || order.customer?.name || 'Unknown',
          email: order.userId?.email || order.customer?.email || order.email || '',
          amount: order.totalAmount || order.amount || 0,
          paid: order.paymentSettlement?.paidAmount || 0,
          balance: order.paymentSettlement?.balanceDue || 0,
          paymentStatus: order.paymentInfo?.status || order.paymentStatus || 'pending',
          status: order.orderStatus || order.status || 'Pending',
          date: new Date(order.createdAt || Date.now()).toLocaleDateString(),
        }));
        setOrderList(orders);
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Failed to load orders';
        setError(msg);
        warning(msg);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [warning, statusFilter]);

  const columns = [
    { key: 'id', label: 'Order ID', sortable: true },
    { key: 'customerName', label: 'Customer', sortable: true },
    { key: 'email', label: 'Email', sortable: false },
    {
      key: 'amount',
      label: 'Amount',
      render: (val) => `₹${Number(val || 0).toLocaleString()}`,
      sortable: true,
    },
    {
      key: 'balance',
      label: 'Balance',
      render: (val) => `Rs. ${Number(val || 0).toLocaleString('en-IN')}`,
      sortable: true,
    },
    {
      key: 'paymentStatus',
      label: 'Payment',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'paid'
            ? 'bg-green-100 text-green-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}>
          {String(val || 'pending').charAt(0).toUpperCase() + String(val || 'pending').slice(1)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'Delivered'
            ? 'bg-green-100 text-green-700'
            : val === 'Dispatched'
            ? 'bg-blue-100 text-blue-700'
            : val === 'Packing'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}>
          {String(val || 'pending').charAt(0).toUpperCase() + String(val || 'pending').slice(1)}
        </span>
      ),
    },
    { key: 'date', label: 'Date', sortable: true },
  ];

  const filteredOrders = orderList.filter(order =>
    columns.some(col =>
      String(order[col.key] || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        description="Manage customer orders and shipments"
        breadcrumbs={['Home', 'Orders']}
      />

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-2">
        {['all', 'Confirmed', 'Packing', 'Packed', 'Dispatched', 'Delivered'].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${statusFilter === status ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-amber-50'}`}
          >
            {status === 'all' ? 'All Orders' : status}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
          <span className="text-gray-600">Loading orders...</span>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredOrders}
          onEdit={(order) => navigate(`/admin/orders/${order.id}`)}
          searchable={true}
          paginate={true}
          itemsPerPage={10}
        />
      )}
    </div>
  );
};

export default Orders;
